import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import Swal from 'sweetalert2';

export default function SubtopicTable() {
  const { parentId } = useParams(); // main_topic_id
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rank from module_name like "1. Real Numbers", "6 Triangles", etc.
  //  -> 1*1000 + minor (if "1.2 …"), names without digits go after numbered ones.
  const rankFromModuleName = (name) => {
    if (!name) return Number.POSITIVE_INFINITY;
    const s = String(name).trim();
    // grab first "major[.minor]" number we see
    const m = s.match(/(\d+)(?:\.(\d+))?/);
    if (!m) return Number.POSITIVE_INFINITY;
    const major = parseInt(m[1], 10);
    const minor = m[2] ? parseInt(m[2], 10) : 0;
    return major * 1000 + minor;
  };

  // For outline rows like "2.10", "2.9"
  const rankFromModuleNumber = (val) => {
    if (val == null || String(val).trim() === '') return Number.POSITIVE_INFINITY;
    const parts = String(val)
      .trim()
      .split('.')
      .map(p => {
        const m = p.match(/\d+/);
        return m ? parseInt(m[0], 10) : 0;
      });
    let rank = 0;
    for (let i = 0; i < parts.length; i++) {
      const power = (parts.length - i - 1);
      rank += parts[i] * Math.pow(1000, power);
    }
    return rank;
  };

  useEffect(() => {
    if (!parentId) return;

    const fetchOutlines = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('outline_summary')
          .select('*')
          .eq('main_topic_id', parentId)
          .order('module_name', { ascending: true }); // server hint; we’ll enforce client order too

        if (error) throw error;

        // Group rows by level‑1 topic
        const topicMap = {};
        (data || []).forEach((item) => {
          const {
            topic_id,
            topic_name,
            outline_id,
            outline_name,
            last_attempted_at,
            time_taken_secs,
            score_percent,
            blog_url,
            quick_review_html,
            module_number,
            module_name, // 👈 we’ll sort with this
          } = item;

          if (!topicMap[topic_id]) {
            topicMap[topic_id] = {
              id: topic_id,
              name: topic_name,
              outlines: [],
              moduleNamesSeen: new Set(), // to compute topic rank
            };
          }

          topicMap[topic_id].outlines.push({
            id: outline_id,
            name: outline_name,
            last_attempted_at,
            time_taken_secs,
            score_percent,
            blog_url: blog_url || null,
            quick_review_html: quick_review_html || null,
            module_number,
            module_name,
          });

          if (module_name) topicMap[topic_id].moduleNamesSeen.add(module_name);
        });

        // Sort outlines within each topic by module_number (natural)
        const topicsArr = Object.values(topicMap).map(t => {
          const outlinesSorted = [...t.outlines].sort(
            (a, b) => rankFromModuleNumber(a.module_number) - rankFromModuleNumber(b.module_number)
          );

          // Topic rank = smallest rank derived from module_name across its rows
          const ranks = [...t.moduleNamesSeen].map(n => rankFromModuleName(n));
          const numbered = ranks.filter(r => Number.isFinite(r) && r !== Number.POSITIVE_INFINITY);
          const topicRank = numbered.length
            ? Math.min(...numbered)
            : Number.POSITIVE_INFINITY;

          // Also keep a representative module_name for stable alpha fallback
          const sampleModuleName = [...t.moduleNamesSeen][0] || '';

          return { ...t, outlines: outlinesSorted, _rank: topicRank, _sampleModuleName: sampleModuleName };
        });

        // Sort topics: numbered first by rank, then alpha by module_name
        topicsArr.sort((a, b) => {
          if (a._rank !== b._rank) return a._rank - b._rank;
          return String(a._sampleModuleName).localeCompare(String(b._sampleModuleName));
        });

        setTopics(topicsArr);

        // restore scroll
        setTimeout(() => {
          const savedY = sessionStorage.getItem('subtopicScrollY');
          if (savedY) {
            window.scrollTo(0, parseInt(savedY, 10));
            sessionStorage.removeItem('subtopicScrollY');
          }
        }, 100);
      } catch (err) {
        console.error('Outline fetch error:', err);
        setTopics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOutlines();
  }, [parentId]);

  if (!parentId) return null;
  if (loading) return <div className="p-4 text-center text-gray-500">Loading topics...</div>;

  const formatDuration = (seconds) => {
    if (seconds == null) return "⏳ Not yet timed";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s < 10 ? '0' : ''}${s}s`;
  };

  const handleBack = () => {
    sessionStorage.setItem('subtopicScrollY', window.scrollY);
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 relative">
      {/* Back Button */}
      <div className="w-full flex justify-end mb-4 sm:mb-8">
        <button
          onClick={handleBack}
          className="bg-indigo-100 hover:bg-indigo-200 text-indigo-800 font-medium px-4 py-2 rounded shadow-sm transition"
        >
          🔙 Dashboard
        </button>
      </div>

      {/* Page Heading */}
      <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center text-indigo-700 tracking-wide">
        🧠 Topic Breakdown
      </h2>

      {topics.length === 0 ? (
        <div className="text-gray-500 text-center">No outlines found under this area.</div>
      ) : (
        topics.map((topic) => (
          <div
            key={topic.id}
            className="mb-10 border border-gray-200 rounded-lg shadow-md p-5 sm:p-6 bg-white hover:shadow-lg transition"
          >
            <h3 className="text-xl sm:text-2xl font-semibold text-indigo-800 mb-3">
              📘 {topic.name}
              <span className="ml-2 text-sm text-indigo-500">
                ({topic.outlines?.length || 0} outlines)
              </span>
            </h3>

            <ul className="space-y-3">
              {(topic.outlines || []).map((outline) => (
                <li
                  key={outline.id}
                  className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-indigo-50 p-3 rounded shadow-sm hover:bg-indigo-100 transition"
                >
                  <div className="text-gray-800 font-medium w-full sm:w-auto mb-2 sm:mb-0">
                    {/* show module_name and module_number if you like */}
                    📄 {outline.module_name ? `${outline.module_name}: ` : ''}
                    {outline.module_number ? `${outline.module_number} — ` : ''}
                    {outline.name}
                    <div className="text-sm text-gray-600 mt-1">
                      🗓️ Last Attempted: {outline.last_attempted_at ? new Date(outline.last_attempted_at).toLocaleDateString() : "Not yet attempted"}
                      <br />
                      ⏱️ Time Taken: {formatDuration(outline.time_taken_secs)}
                      <br />
                      📊 Score: {outline.score_percent != null ? `${outline.score_percent}%` : "💡 Give it a shot!"}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      className="bg-blue-600 text-white px-4 py-1 rounded text-sm hover:bg-blue-700 transition"
                      onClick={() => {
                        sessionStorage.setItem('subtopicScrollY', window.scrollY);
                        navigate(`/quiz/${outline.id}`);
                      }}
                    >
                      Take Quiz
                    </button>

                    <button
                      className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 transition"
                      onClick={() => {
                        sessionStorage.setItem('subtopicScrollY', window.scrollY);
                        navigate(`/learn/${outline.id}`);
                      }}
                    >
                      📘 Learn internal
                    </button>

                    <button
                      className="bg-green-600 text-white px-4 py-1 rounded text-sm hover:bg-green-700 transition"
                      onClick={() => {
                        if (outline.blog_url) {
                          sessionStorage.setItem('subtopicScrollY', window.scrollY);
                          window.open(outline.blog_url, '_blank');
                        } else {
                          Swal.fire({
                            icon: 'info',
                            title: '🚧 Coming Soon!',
                            text: 'Blog article is in progress. Stay in touch!',
                          });
                        }
                      }}
                    >
                      📘 Learn
                    </button>

                    <button
                      className="bg-yellow-600 text-white px-4 py-1 rounded text-sm hover:bg-yellow-700 transition"
                      onClick={() => {
                        sessionStorage.setItem('subtopicScrollY', window.scrollY);
                        if (outline.quick_review_html) {
                          navigate(`/quickreview/${outline.id}`);
                        } else {
                          Swal.fire({
                            icon: 'info',
                            title: '⚡ Coming Soon!',
                            text: 'Quick Review is in progress. Stay tuned!',
                          });
                        }
                      }}
                    >
                      ⚡ Quick Review
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}
